/**
 * host_status plugin client — central-server cron health.
 *
 * BE: `services/plugins/host_status/` (decoupler's commit 6ca95aa).
 * Auth: JWTAuthentication + superuser. The Sidebar entry that mounts
 * `HostInfraPage` is already gated by `SuperuserGuard`, so the FE
 * doesn't need a defense-in-depth tooltip.
 *
 * Probe semantics (per `tools/cron_status.py`):
 * - `probe_kind === 'fresh'` — dump-file mtime must be ≤
 *   `fresh_seconds_ago`. Older = STALE, missing = MISSING.
 * - `probe_kind === 'exists'` — file existence is enough. (Not used
 *   today; reserved for cron-quiet daemons that DO write an
 *   applied-log heartbeat.)
 * - `probe_kind === 'never'` — no probe wired; the cron is
 *   cron-quiet-by-design (e.g. `add_wg_peer.sh`,
 *   `generate_hosts_from_wg.py`) or its effect is tracked elsewhere
 *   (e.g. `sync_lc_pubkeys_dev` heartbeats via RootScriptHeartbeat
 *   on linza-dev — task #92). Status is `UNKNOWN` and the FE
 *   renders it as OK (healthy) — "always-OK-unless-explicitly-broken"
 *   per team-lead 2026-05-21.
 *
 * The BE's `overall` rollup already treats UNKNOWN as benign.
 */
import { axiosInstance } from './api';

export type CronStatus = 'OK' | 'STALE' | 'MISSING' | 'UNKNOWN';
export type CronProbeKind = 'fresh' | 'exists' | 'never';
export type OverallStatus = 'OK' | 'DRIFT';
/** Per-server key for the BE's `?server=` query arg (task #87).
 *  - `depl` — central deployments server (default; pre-#87 behaviour).
 *  - `dev`  — linza-dev, the reverse-SSH terminator. */
export type ServerKey = 'depl' | 'dev';

export interface CronCheckRow {
  /** Stable machine-readable id, e.g. "dump_wg_peers". Used by the
   *  FE both as the React key and as the i18n purpose-key suffix. */
  id: string;
  /** Script basename, e.g. "dump_wg_peers.sh". */
  label: string;
  /** Human-readable purpose, in English. FE owns localisation via i18n
   *  key `system.hostInfra.purpose.<id>` — this field is a fallback for
   *  any new BE-side row the FE hasn't yet learned about. */
  purpose: string;
  /** The expected line in `ops/system-crontab.lines.txt`. Rendered as
   *  a code snippet for the operator if/when we add an "expand row"
   *  affordance. */
  expected_line: string;
  /** Probe target under `/var/lib/linza/`, e.g. "authorized_keys.example". */
  probe_file: string | null;
  probe_kind: CronProbeKind;
  /** Threshold in seconds for `fresh` probes. */
  fresh_seconds_ago: number;
  status: CronStatus;
  mtime_iso: string | null;
  mtime_seconds_ago: number | null;
}

export interface CronStatusResponse {
  /** Echoes the resolved `?server=` value the BE used (task #87) —
   *  helpful when chaining or sanity-checking. Optional for back-compat
   *  with pre-#87 BE versions that don't include it. */
  server?: ServerKey;
  overall: OverallStatus;
  checks: CronCheckRow[];
}

export const getCronStatus = (server: ServerKey = 'depl') =>
  axiosInstance.get<CronStatusResponse>(
    server === 'depl'
      ? '/host_status/cron-status/'
      : `/host_status/cron-status/?server=${server}`,
  );

/** Response shape from ``GET /host_status/install-curl/``.
 *
 *  Each call returns a FRESH curl one-liner carrying a short-lived
 *  signed `install_token` in the URL (Django TimestampSigner; ~10
 *  min TTL). The operator copies it, pastes in root shell on the
 *  target host, and runs. No flags, no manual token entry — the BE
 *  bakes the LC_PUBKEYS_TOKEN value into the served script body.
 */
export interface HostInstallCurlResponse {
  install_curl: string;
}

export const getHostInstallCurl = (server: ServerKey) =>
  axiosInstance.get<HostInstallCurlResponse>(
    `/host_status/install-curl/?server=${server}`,
  );

/** One row from ``GET /local_computers/root-scripts/heartbeats/`` — the push-based
 *  heartbeat per canonical host cron. `model_deploy` carries the rich payload
 *  (log_tail + deployed_versions); the dump_* crons leave those empty. */
export interface RootScriptHeartbeat {
  name: string;
  label: string;
  purpose: string;
  cron_quiet: boolean;
  fresh_seconds: number;
  ran_at: string | null;
  rc: number | null;
  summary: string;
  log_tail: string;
  deployed_versions: Record<string, string>;
  seconds_since_ran: number | null;
  status: 'OK' | 'STALE' | 'MISSING';
}

export const getRootScriptHeartbeats = () =>
  axiosInstance.get<{ heartbeats: RootScriptHeartbeat[] }>(
    '/local_computers/root-scripts/heartbeats/',
  );
